/**
 * RemedialRepairPresetsAdmin — Iter161 Phase A.
 *
 * Admin page for managing the three preset lists that drive the
 * Remedial Repairs builder's three key dropdowns:
 *   - Repair Type   (cascaded by Roof Type)
 *   - Location      (flat list)
 *   - Description   (cascaded by Repair Type)
 *
 * Backed by:
 *   - GET    /api/remedial-repairs/presets?kind=X
 *   - POST   /api/remedial-repairs/presets
 *   - PUT    /api/remedial-repairs/presets/{id}
 *   - DELETE /api/remedial-repairs/presets/{id}
 *   - POST   /api/remedial-repairs/presets/seed
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Loader2, ChevronLeft, Wrench, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROOF_TYPES = ['', 'Concrete', 'Iron', 'Decramastic', 'ColourTile', 'Other'];

const TABS = [
  { key: 'repair_type', label: 'Repair Type', cascadeLabel: 'Cascades by Roof Type', supportsRoofParent: true,  supportsRepairParent: false },
  { key: 'location',    label: 'Location',    cascadeLabel: 'Flat list — no cascade',  supportsRoofParent: false, supportsRepairParent: false },
  { key: 'description', label: 'Description', cascadeLabel: 'Cascades by Repair Type',  supportsRoofParent: false, supportsRepairParent: true },
];

export default function RemedialRepairPresetsAdmin() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  // Iter166 — accept ?kind=repair_type|location|description so deep
  // links from the Remedial Repairs builder land directly on the
  // right tab. Defaults to repair_type when no/unknown param.
  const initialKind = (() => {
    if (typeof window === 'undefined') return 'repair_type';
    const k = new URLSearchParams(window.location.search).get('kind');
    return ['repair_type', 'location', 'description'].includes(k) ? k : 'repair_type';
  })();
  const [activeKind, setActiveKind] = useState(initialKind);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [repairTypeOptions, setRepairTypeOptions] = useState([]); // for description's parent picker
  const [newRow, setNewRow] = useState({ name: '', parent_roof_type: '', parent_repair_type: '' });
  const [savingId, setSavingId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('rtr_token') || localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, rtRes] = await Promise.all([
        axios.get(`${API}/remedial-repairs/presets?kind=${activeKind}`, { headers: authHeader() }),
        // Description kind also needs the list of all repair_types as
        // parent-picker options. Cheap extra fetch.
        activeKind === 'description'
          ? axios.get(`${API}/remedial-repairs/presets?kind=repair_type`, { headers: authHeader() })
          : Promise.resolve({ data: { presets: [] } }),
      ]);
      setRows(res.data?.presets || []);
      if (activeKind === 'description') {
        const uniq = Array.from(new Set((rtRes.data?.presets || []).map(p => p.name))).sort();
        setRepairTypeOptions(uniq);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load presets');
    } finally {
      setLoading(false);
    }
  }, [activeKind]);

  useEffect(() => { load(); }, [load]);

  const currentTab = useMemo(() => TABS.find(t => t.key === activeKind), [activeKind]);

  // Group rows by parent (Roof Type for repair_type kind, Repair Type
  // for description kind, single group for location).
  const grouped = useMemo(() => {
    if (activeKind === 'location') return [{ parent: '', label: 'All locations', rows }];
    if (activeKind === 'repair_type') {
      const buckets = {};
      rows.forEach(r => {
        const k = r.parent_roof_type || '(Any roof type)';
        (buckets[k] = buckets[k] || []).push(r);
      });
      return Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b)).map(([parent, rs]) => ({
        parent, label: parent, rows: rs,
      }));
    }
    // description
    const buckets = {};
    rows.forEach(r => {
      const k = r.parent_repair_type || '(Any repair type — global)';
      (buckets[k] = buckets[k] || []).push(r);
    });
    return Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b)).map(([parent, rs]) => ({
      parent, label: parent, rows: rs,
    }));
  }, [rows, activeKind]);

  const onUpdateRow = (id, patch) => {
    setRows(rs => rs.map(r => r._id === id ? { ...r, ...patch, _dirty: true } : r));
  };

  const saveRow = async (row) => {
    setSavingId(row._id);
    try {
      const payload = {
        kind: row.kind,
        name: row.name,
        parent_roof_type:   row.parent_roof_type || '',
        parent_repair_type: row.parent_repair_type || '',
        sort_order: row.sort_order || 0,
        is_default: !!row.is_default,
      };
      const { data } = await axios.put(`${API}/remedial-repairs/presets/${row._id}`, payload, { headers: authHeader() });
      setRows(rs => rs.map(r => r._id === row._id ? { ...data, _dirty: false } : r));
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Delete preset "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/remedial-repairs/presets/${row._id}`, { headers: authHeader() });
      setRows(rs => rs.filter(r => r._id !== row._id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const addNew = async () => {
    const name = (newRow.name || '').trim();
    if (!name) { toast.error('Name required'); return; }
    try {
      const payload = {
        kind: activeKind,
        name,
        parent_roof_type:   currentTab.supportsRoofParent   ? (newRow.parent_roof_type || '')   : '',
        parent_repair_type: currentTab.supportsRepairParent ? (newRow.parent_repair_type || '') : '',
        sort_order: rows.length,
      };
      const { data } = await axios.post(`${API}/remedial-repairs/presets`, payload, { headers: authHeader() });
      setRows(rs => [...rs, data]);
      setNewRow({ name: '', parent_roof_type: '', parent_repair_type: '' });
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };

  const onSeed = async () => {
    if (!window.confirm('Re-run the seed? Defaults are inserted only if missing (your custom presets are kept).')) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/remedial-repairs/presets/seed?force=true`, null, { headers: authHeader() });
      toast.success(`Inserted ${data.inserted}, skipped ${data.skipped} existing.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="repair-presets-admin-page">
        <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-rose-700 text-sm">
          Admin access required to manage Remedial Repairs presets.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-6xl mx-auto" data-testid="repair-presets-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/appraisals" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"><ChevronLeft size={12} /> Back to Appraisals</Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 font-[Manrope]">
            <Wrench size={20} className="text-rose-600" /> Remedial Repairs Presets
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Manage the three preset lists that drive the Remedial Repairs builder's three key dropdowns.
            Cascade rules — <strong>Repair Type</strong> cascades by Roof Type, <strong>Description</strong> cascades by Repair Type,
            <strong> Location</strong> is a flat list. Empty parent = shows for everyone.
          </p>
        </div>
        <button
          onClick={onSeed}
          disabled={seeding}
          data-testid="reseed-presets-btn"
          className="text-xs border border-slate-300 bg-white text-slate-700 rounded-md px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1 self-start disabled:opacity-50"
          title="Re-run the seed — defaults are inserted only if missing"
        >
          {seeding ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-seed defaults
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={`preset-tab-${t.key}`}
            onClick={() => setActiveKind(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              activeKind === t.key
                ? 'border-rose-600 text-rose-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="text-xs text-slate-500">{currentTab?.cascadeLabel}</div>

      {/* Add new row */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap items-end gap-2" data-testid="preset-add-bar">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Name</label>
          <input
            data-testid="preset-add-name"
            type="text"
            value={newRow.name}
            onChange={(e) => setNewRow(r => ({ ...r, name: e.target.value }))}
            placeholder={`New ${currentTab?.label.toLowerCase()}…`}
            className="w-full mt-0.5 text-sm border border-slate-300 rounded px-2 py-1.5"
            onKeyDown={(e) => { if (e.key === 'Enter') addNew(); }}
          />
        </div>
        {currentTab?.supportsRoofParent && (
          <div className="min-w-[160px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Cascades for Roof Type</label>
            <select
              data-testid="preset-add-roof-parent"
              value={newRow.parent_roof_type}
              onChange={(e) => setNewRow(r => ({ ...r, parent_roof_type: e.target.value }))}
              className="w-full mt-0.5 text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              {ROOF_TYPES.map(t => <option key={t} value={t}>{t || '(Any — global)'}</option>)}
            </select>
          </div>
        )}
        {currentTab?.supportsRepairParent && (
          <div className="min-w-[220px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Cascades for Repair Type</label>
            <select
              data-testid="preset-add-repair-parent"
              value={newRow.parent_repair_type}
              onChange={(e) => setNewRow(r => ({ ...r, parent_repair_type: e.target.value }))}
              className="w-full mt-0.5 text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="">(Any — global)</option>
              {repairTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        <button
          data-testid="preset-add-btn"
          onClick={addNew}
          className="bg-rose-600 text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-rose-700 flex items-center gap-1"
        >
          <Plus size={14} /> Add
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          No presets yet. Add the first one above, or hit <strong>Re-seed defaults</strong>.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(g => (
            <div key={g.parent} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`preset-group-${g.parent || 'global'}`}>
              <div className="bg-slate-100 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center justify-between">
                <span>{g.label}</span>
                <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">{g.rows.length} preset{g.rows.length === 1 ? '' : 's'}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-400">
                  <tr>
                    <th className="text-left px-3 py-1.5">Name</th>
                    {currentTab?.supportsRoofParent && <th className="text-left px-3 py-1.5 w-40">Roof Type</th>}
                    {currentTab?.supportsRepairParent && <th className="text-left px-3 py-1.5 w-56">Repair Type</th>}
                    <th className="text-right px-3 py-1.5 w-20">Sort</th>
                    <th className="text-right px-3 py-1.5 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {g.rows.map(row => (
                    <tr key={row._id} className={row._dirty ? 'bg-yellow-50' : 'hover:bg-slate-50/40'} data-testid={`preset-row-${row._id}`}>
                      <td className="px-3 py-1.5">
                        <input
                          data-testid={`preset-name-${row._id}`}
                          value={row.name}
                          onChange={(e) => onUpdateRow(row._id, { name: e.target.value })}
                          className="w-full text-sm border border-slate-200 rounded px-2 py-1"
                        />
                      </td>
                      {currentTab?.supportsRoofParent && (
                        <td className="px-3 py-1.5">
                          <select
                            data-testid={`preset-roof-${row._id}`}
                            value={row.parent_roof_type || ''}
                            onChange={(e) => onUpdateRow(row._id, { parent_roof_type: e.target.value })}
                            className="w-full text-sm border border-slate-200 rounded px-2 py-1 bg-white"
                          >
                            {ROOF_TYPES.map(t => <option key={t} value={t}>{t || '(Any — global)'}</option>)}
                          </select>
                        </td>
                      )}
                      {currentTab?.supportsRepairParent && (
                        <td className="px-3 py-1.5">
                          <select
                            data-testid={`preset-rt-${row._id}`}
                            value={row.parent_repair_type || ''}
                            onChange={(e) => onUpdateRow(row._id, { parent_repair_type: e.target.value })}
                            className="w-full text-sm border border-slate-200 rounded px-2 py-1 bg-white"
                          >
                            <option value="">(Any — global)</option>
                            {repairTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                      )}
                      <td className="px-3 py-1.5 text-right">
                        <input
                          data-testid={`preset-sort-${row._id}`}
                          type="number"
                          value={row.sort_order || 0}
                          onChange={(e) => onUpdateRow(row._id, { sort_order: parseInt(e.target.value, 10) || 0 })}
                          className="w-16 text-right text-sm border border-slate-200 rounded px-2 py-1"
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right space-x-1">
                        <button
                          data-testid={`preset-save-${row._id}`}
                          onClick={() => saveRow(row)}
                          disabled={!row._dirty || savingId === row._id}
                          className="bg-rose-600 text-white text-xs font-semibold rounded px-2 py-1 hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-1"
                        >
                          {savingId === row._id ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                          Save
                        </button>
                        <button
                          data-testid={`preset-delete-${row._id}`}
                          onClick={() => removeRow(row)}
                          className="bg-slate-200 text-slate-700 text-xs font-semibold rounded px-2 py-1 hover:bg-rose-100 hover:text-rose-700 inline-flex items-center gap-1"
                        >
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
